/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Button,
  Box,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Auth } from "aws-amplify";
import { primaryColor, COGNITO_GROUPS_ALLOWED } from "resources/constants";
import ScaleLoader from "react-spinners/ScaleLoader";
import useFormFields from "lib/forms";
import { useIsMounted } from "lib/react/customHooks";
import LoginComponent from "./LoginComponent";

const useStyles = makeStyles(() => ({
  alertBox: {
    boxShadow:
      "rgba(9, 30, 66, 0.28) 0px 4px 8px -2px, rgba(9, 30, 66, 0.3) 0px 0px 1px;",
    fontSize: "12px",
    lineHeight: "20px",
    marginBottom: "16px",
    transform: "rotateX(0deg)",
    transformOrigin: "50% 0px",
    transitionProperty:
      "visibility, height, margin-bottom, opacity, transform, padding",
    transitionDuration: "0s, 0.2s, 0.2s, 0.2s, 0.2s",
    transitionTimingFunction: "ease-in-out",
    borderRadius: "3px",
    padding: "16px"
  },
  formTitle: {
    marginBottom: "15px"
  },
  formButton: {
    marginTop: "20px"
  },
  recoveryMesage: {
    fontSize: "12px"
  },
  sendCodeTextField: {
    marginTop: "5px"
  },
  spinnerContainer: {
    textAlign: "center"
  }
}));

export default function SignInComponent() {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const isMounted = useIsMounted();
  const [displayNewPasswordForm, setDisplayNewPasswordForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cognitoUser, setCognitoUser] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [formTitle, setFormTitle] = useState("");

  const FormTitle = ({ children }) => {
    return (
      <Typography
        align="center"
        component="h1"
        variant="h6"
        classes={{ h6: classes.formTitle }}
      >
        {children}
      </Typography>
    );
  };

  const LoadingSpinner = () => {
    return (
      <Box className={classes.spinnerContainer}>
        <ScaleLoader size={150} color={primaryColor} loading />
      </Box>
    );
  };

  const SignInForm = () => {
    const [fields, handleFieldChange] = useFormFields({
      username: "",
      password: ""
    });

    useEffect(() => {
      setFormTitle("Log in to Athena");
    }, [formTitle]);

    function handleSubmit(event) {
      event.preventDefault();
      setIsLoading(true);
      Auth.signIn(fields.username, fields.password)
        .then(user => {
          setCognitoUser(user);
          if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
            setAlertMessage("");

            setDisplayNewPasswordForm(true);
          } else if (
            !user.signInUserSession.accessToken.payload["cognito:groups"] ||
            !user.signInUserSession.accessToken.payload[
              "cognito:groups"
            ].includes(COGNITO_GROUPS_ALLOWED)
          ) {
            setAlertMessage("Authentication failure");
          } else {
            history.push({ pathname: "/" });
          }
        })
        .catch(err => {
          setAlertMessage(err.message);
        })
        .finally(() => {
          // On success we navigate away and this component unmounts, so guard
          // the state update to avoid a leak warning after redirect.
          if (isMounted()) setIsLoading(false);
        });
    }

    return (
      <>
        <form onSubmit={handleSubmit}>
          {((location || {}).state || {}).passwordReset === true && (
            <div className={classes.alertBox}>
              Password successfully changed!
            </div>
          )}
          {((location || {}).state || {}).sessionExpired === true && (
            <div className={classes.alertBox}>
              Your session has expired. Please log in again
            </div>
          )}
          <TextField
            variant="outlined"
            size="small"
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            onChange={handleFieldChange}
          />
          <TextField
            variant="outlined"
            size="small"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            onChange={handleFieldChange}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            className={classes.formButton}
          >
            Sign In
          </Button>
        </form>
      </>
    );
  };

  const NewPasswordForm = () => {
    const [fields, handleFieldChange] = useFormFields({
      password: "",
      confirmPassword: ""
    });

    useEffect(() => {
      setFormTitle("First time here?");
    }, [formTitle]);

    const handleSetNewPasswordSubmit = event => {
      event.preventDefault();

      if (fields.confirmPassword !== fields.password) {
        setAlertMessage("Passwords provided do not match.");
        return false;
      }

      if (fields.password.length < 6) {
        setAlertMessage("Password must contain at least 6 characters.");
        return false;
      }

      Auth.completeNewPassword(
        cognitoUser, // the Cognito User Object
        fields.password // the new password
      )
        .then(() => {
          history.push({ pathname: "/" });
        })
        .catch(err => {
          setAlertMessage(err.message);
        });

      return false;
    };

    return (
      <>
        <Typography variant="body1" classes={{ body1: classes.recoveryMesage }}>
          Change your temporary password before proceeding
        </Typography>
        <form onSubmit={handleSetNewPasswordSubmit}>
          <TextField
            variant="outlined"
            size="small"
            margin="normal"
            classes={{ root: classes.sendCodeTextField }}
            required
            fullWidth
            name="password"
            label="New password"
            type="password"
            id="password"
            autoComplete="off"
            onChange={handleFieldChange}
          />
          <TextField
            variant="outlined"
            size="small"
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm password"
            type="password"
            id="confirmPassword"
            autoComplete="off"
            onChange={handleFieldChange}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.formButton}
          >
            Change Password and proceed
          </Button>
        </form>
      </>
    );
  };

  return (
    <LoginComponent
      dividerLinkTo="/forgotPassword"
      dividerLinkText="Forgot password?"
      showLinks={!isLoading}
    >
      <FormTitle>{formTitle}</FormTitle>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {alertMessage && (
            <div className={classes.alertBox}>{alertMessage}</div>
          )}
          {displayNewPasswordForm ? <NewPasswordForm /> : <SignInForm />}
        </>
      )}
    </LoginComponent>
  );
}
